import { useEffect, useState } from 'react';

/** One selectable khoroo, with the express zone it belongs to (null when it has no active zone). */
export interface DeliveryArea {
    id: number
    town: string
    district: string
    unit: string
    zoneId: number | null
    zoneName: string | null
    expressFee: number | null
}

let cache: Promise<DeliveryArea[]> | null = null;

/** The list rarely changes, so every form on a page load shares one request. */
export function fetchDeliveryAreas(): Promise<DeliveryArea[]> {
    if (!cache) {
        cache = fetch(`${process.env.REACT_APP_API_URL}delivery-areas`, { headers: { Accept: 'application/json' } })
            .then((res) => {
                if (!res.ok) {
                    throw new Error(`delivery-areas HTTP ${res.status}`);
                }
                return res.json();
            })
            .then((json) => json.data as DeliveryArea[])
            .catch((err) => {
                cache = null;
                throw err;
            });
    }
    return cache;
}

/** Admin screens change zones/areas, so they drop the shared copy after saving. */
export function resetDeliveryAreasCache() {
    cache = null;
}

export function useDeliveryAreas(): { areas: DeliveryArea[], loading: boolean, error: boolean } {
    const [state, setState] = useState<{ areas: DeliveryArea[], loading: boolean, error: boolean }>({ areas: [], loading: true, error: false });

    useEffect(() => {
        let active = true;
        fetchDeliveryAreas()
            .then((areas) => active && setState({ areas, loading: false, error: false }))
            .catch((err) => {
                console.error(err);
                active && setState({ areas: [], loading: false, error: true });
            });
        return () => {
            active = false;
        };
    }, []);

    return state;
}

function unique(values: string[]): string[] {
    return Array.from(new Set(values));
}

export function townsOf(areas: DeliveryArea[]): string[] {
    return unique(areas.map((a) => a.town));
}

export function districtsOf(areas: DeliveryArea[], town: string): string[] {
    return unique(areas.filter((a) => a.town === town).map((a) => a.district));
}

/** The khoroo of one district; each one is what gets saved (by id) when it is picked. */
export function areasOf(areas: DeliveryArea[], town: string, district: string): DeliveryArea[] {
    return areas.filter((a) => a.town === town && a.district === district);
}

export function findAreaById(areas: DeliveryArea[], id: number | null | undefined): DeliveryArea | undefined {
    return id === null || id === undefined ? undefined : areas.find((a) => a.id === id);
}
